import { Container } from '@playcanvas/pcui';
import { Vec3 } from 'playcanvas';

import { Events } from '../events';
import { pointerPositionInElement } from './pointer-coordinates';
import { Scene } from '../scene';
import {
    ANNOTATION_PRIORITY_FLAG_CLASSES,
    AnnotationPriority,
    buildAnnotationPriorityReadonly,
    buildAnnotationPrioritySelect,
    getAnnotationPriorityFieldLabel,
    normalizeAnnotationPriority,
    normalizeCommerceAnnotationHighlight,
    priorityToFlagClass
} from '../ui/annotation-priority';
import { RfiPanel, RfiRecord } from '../ui/rfi-panel';
import { isVolugraphCommerceMode } from '../volugraph-commerce';

const screenVec = new Vec3();

/** Camera-to-pin distance / sceneRadius above which markers shrink to dots (less overlap when zoomed out). */
const RFI_COMPACT_DISTANCE_RATIO = 1.55;

function cameraViewFromScene(scene: Scene): { position: number[]; target: number[] } | null {
    const pos = scene.camera.mainCamera?.getPosition?.();
    const fp = scene.camera.focalPointTween?.value;
    if (!pos || !fp) return null;
    return {
        position: [pos.x, pos.y, pos.z],
        target: [fp.x, fp.y, fp.z]
    };
}

function genId(): string {
    return `rfi_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function isCommerceAnnotationMode(): boolean {
    return isVolugraphCommerceMode();
}

let activeLightboxEl: HTMLElement | null = null;
let activeLightboxEsc: ((e: KeyboardEvent) => void) | null = null;

function closeAnnotationImageLightbox() {
    if (activeLightboxEsc) {
        document.removeEventListener('keydown', activeLightboxEsc);
        activeLightboxEsc = null;
    }
    if (activeLightboxEl) {
        activeLightboxEl.remove();
        activeLightboxEl = null;
    }
}

function openAnnotationImageLightbox(imageUrl: string, _host: HTMLElement) {
    closeAnnotationImageLightbox();
    const overlay = document.createElement('div');
    overlay.className = 'annotation-image-lightbox';
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeAnnotationImageLightbox();
    });

    const frame = document.createElement('div');
    frame.className = 'annotation-image-lightbox-frame';
    frame.addEventListener('click', (e) => e.stopPropagation());

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'annotation-image-lightbox-close';
    closeBtn.setAttribute('aria-label', 'Close image');
    closeBtn.textContent = '\u00d7';
    closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closeAnnotationImageLightbox();
    });

    const img = document.createElement('img');
    img.className = 'annotation-image-lightbox-img';
    img.src = imageUrl;
    img.alt = 'Annotation image';
    img.addEventListener('click', (e) => e.stopPropagation());

    frame.appendChild(closeBtn);
    frame.appendChild(img);
    overlay.appendChild(frame);
    document.body.appendChild(overlay);
    activeLightboxEl = overlay;
    activeLightboxEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeAnnotationImageLightbox();
    };
    document.addEventListener('keydown', activeLightboxEsc);
}

/**
 * Pinned annotations (formerly RFIs): click on splat surface to place a 3D pin; edit details via hover card.
 * Persists via Volugraph React + Firestore (`volugraph.rfi` events — kept for backward compat).
 */
class RfiTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const panel = new RfiPanel(events);
        canvasContainer.append(panel);

        /** Bumps on local annotation mutations (not selection-only). Paired with `lastAnnotationSaveCursor` after save/flush. */
        let annotationsDirtyCounter = 0;
        let lastAnnotationSaveCursor = 0;
        const bumpAnnotationDirty = () => {
            annotationsDirtyCounter++;
        };

        events.removeFunction('annotation.dirty');
        events.removeFunction('annotation.markClean');
        events.function('annotation.dirty', () => annotationsDirtyCounter !== lastAnnotationSaveCursor);
        events.function('annotation.markClean', () => {
            lastAnnotationSaveCursor = annotationsDirtyCounter;
        });
        events.on('doc.saved', () => {
            lastAnnotationSaveCursor = annotationsDirtyCounter;
        });
        /** Coordinate markers share Firestore path but bump the same dirty cursor (CoordTool fires this). */
        events.on('annotation.requestBumpDirty', bumpAnnotationDirty);

        const markersHost = document.createElement('div');
        markersHost.className = 'rfi-markers-host';
        markersHost.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:35;';
        canvasContainer.dom.appendChild(markersHost);

        const records: RfiRecord[] = [];
        let selectedId: string | null = null;

        const markerEls = new Map<string, HTMLButtonElement>();
        let active = false;
        let clickGate = false;
        /** Skip the click that follows placing a new pin (pointerup selects; click would otherwise deselect). */
        let suppressCanvasDeselectClick = false;
        let isEditMode = false;

        // Annotation label card (shown only when marker is selected)
        let hoverCardEl: HTMLDivElement | null = null;
        let hoverCardRecId: string | null = null;
        const imageUploadingIds = new Set<string>();
        const hoverImageRowRefresh = new Map<string, () => void>();

        const lightboxHost = () => markersHost.parentElement ?? markersHost;

        const fireCountChanged = () => {
            events.fire('annotation.countChanged', records.length);
        };

        const displayIndexForId = (id: string) => {
            const i = records.findIndex((x) => x.id === id);
            return i >= 0 ? i + 1 : 1;
        };

        const resolveAnnotationStatus = (s: unknown): RfiRecord['status'] => (
            isCommerceAnnotationMode()
                ? normalizeCommerceAnnotationHighlight(s)
                : normalizeAnnotationPriority(s)
        );

        const statusToFlagClass = (s: RfiRecord['status']) => priorityToFlagClass(resolveAnnotationStatus(s));

        const FLAG_CLASSES = ANNOTATION_PRIORITY_FLAG_CLASSES;

        const updateMarkerFlag = (rec: RfiRecord) => {
            const el = markerEls.get(rec.id);
            const numEl = el?.querySelector('.annotation-marker-number');
            if (!numEl) return;
            numEl.classList.remove(...FLAG_CLASSES);
            numEl.classList.add(statusToFlagClass(rec.status));
        };

        const normalizeImportedStatus = (o: unknown): RfiRecord['status'] => resolveAnnotationStatus(o);

        const renumberMarkers = () => {
            records.forEach((r, i) => {
                const el = markerEls.get(r.id);
                if (!el) return;
                const numEl = el.querySelector('.annotation-marker-number');
                if (numEl) numEl.textContent = String(i + 1);
            });
        };

        const syncPanel = () => {
            panel.rebuildList(records, selectedId);
            updateMarkerLabels();
            events.fire('annotation.selectionChanged');
        };

        const updateMarkerLabels = () => {
            records.forEach((r) => {
                const el = markerEls.get(r.id);
                if (!el) return;
                el.classList.toggle('is-selected', r.id === selectedId);
            });
        };

        const setSelected = (id: string | null) => {
            const prevId = selectedId;
            selectedId = id;
            panel.selectRecord(records, id);
            markerEls.forEach((el, mid) => {
                el.classList.toggle('is-selected', mid === id);
            });
            events.fire('annotation.selectionChanged');
            if (!isEditMode) {
                if (id) {
                    const rec = records.find((x) => x.id === id);
                    events.fire('volugraph.rfi', {
                        action: 'select',
                        id,
                        record: rec ? { ...rec } : undefined
                    });
                } else if (prevId) {
                    events.fire('volugraph.rfi', { action: 'deselect', id: prevId });
                }
            }
            if (id) {
                const el = markerEls.get(id);
                if (el) {
                    showHoverCard(id, el);
                }
            } else {
                hideHoverCard();
            }
        };

        const upsertRecord = (rec: RfiRecord, fromRemote: boolean) => {
            const idx = records.findIndex((x) => x.id === rec.id);
            if (idx >= 0) {
                records[idx] = rec;
            } else {
                records.push(rec);
            }
            ensureMarker(rec);
            updateMarkerFlag(rec);
            renumberMarkers();
            syncPanel();
            fireCountChanged();
            if (!fromRemote) {
                bumpAnnotationDirty();
                events.fire('volugraph.rfi', {
                    action: 'upsert',
                    record: { ...rec }
                });
            }
            // Markers are DOM overlays positioned in postrender; splat state may be unchanged so
            // the app skips rendering — force a frame + sync positions so new pins appear immediately.
            scene.forceRender = true;
            updateMarkerPositions();
            applyMarkersGlobalVisibility();
        };

        const removeRecord = (id: string, fromRemote: boolean) => {
            const idx = records.findIndex((x) => x.id === id);
            if (idx >= 0) records.splice(idx, 1);
            const el = markerEls.get(id);
            el?.remove();
            markerEls.delete(id);
            if (selectedId === id) {
                setSelected(null);
            }
            renumberMarkers();
            syncPanel();
            fireCountChanged();
            if (!fromRemote) {
                bumpAnnotationDirty();
                events.fire('volugraph.rfi', { action: 'delete', id });
            }
            scene.forceRender = true;
            updateMarkerPositions();
        };

        const positionHoverCard = (anchorEl: HTMLElement) => {
            if (!hoverCardEl) return;
            const hostRect = markersHost.getBoundingClientRect();
            if (hostRect.width === 0 || hostRect.height === 0) return;

            const numEl = anchorEl.querySelector('.annotation-marker-number') as HTMLElement | null;
            const circleRect = numEl?.getBoundingClientRect() ?? anchorEl.getBoundingClientRect();
            const gap = 8;
            const left = circleRect.right - hostRect.left + gap;
            const top = circleRect.top + circleRect.height / 2 - hostRect.top;
            hoverCardEl.style.left = `${left}px`;
            hoverCardEl.style.top = `${top}px`;
        };

        // ---- Hover card helpers ----
        const buildEditImageRow = (rec: RfiRecord) => {
            const row = document.createElement('div');
            row.className = 'annotation-hover-image-row';
            row.dataset.annotationImageRow = rec.id;

            const render = () => {
                row.innerHTML = '';
                const isUploading = imageUploadingIds.has(rec.id);
                if (isUploading) {
                    const ph = document.createElement('div');
                    ph.className = 'annotation-hover-image-uploading';
                    const spinner = document.createElement('span');
                    spinner.className = 'annotation-hover-image-spinner';
                    ph.appendChild(spinner);
                    const cancel = document.createElement('button');
                    cancel.type = 'button';
                    cancel.className = 'annotation-hover-image-cancel';
                    cancel.textContent = 'Cancel';
                    cancel.addEventListener('click', (e) => {
                        e.stopPropagation();
                        imageUploadingIds.delete(rec.id);
                        render();
                    });
                    ph.appendChild(cancel);
                    row.appendChild(ph);
                    return;
                }
                if (rec.imageUrl) {
                    const wrap = document.createElement('div');
                    wrap.className = 'annotation-hover-image-thumb-wrap';
                    const thumb = document.createElement('button');
                    thumb.type = 'button';
                    thumb.className = 'annotation-hover-image-thumb';
                    const img = document.createElement('img');
                    img.src = rec.imageUrl;
                    img.alt = '';
                    thumb.appendChild(img);
                    thumb.addEventListener('click', (e) => {
                        e.stopPropagation();
                        openAnnotationImageLightbox(rec.imageUrl, lightboxHost());
                    });
                    const removeBtn = document.createElement('button');
                    removeBtn.type = 'button';
                    removeBtn.className = 'annotation-hover-image-remove';
                    removeBtn.textContent = '\u00D7';
                    removeBtn.title = 'Remove image';
                    removeBtn.setAttribute('aria-label', 'Remove image');
                    removeBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        events.fire('volugraph.annotation.removeImage', {
                            id: rec.id,
                            imagePath: rec.imagePath ?? ''
                        });
                    });
                    wrap.appendChild(thumb);
                    wrap.appendChild(removeBtn);
                    row.appendChild(wrap);
                    return;
                }
                const attachBtn = document.createElement('button');
                attachBtn.type = 'button';
                attachBtn.className = 'annotation-hover-image-attach';
                attachBtn.textContent = '+ Attach image';
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = 'image/*';
                fileInput.hidden = true;
                attachBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    fileInput.click();
                });
                fileInput.addEventListener('change', () => {
                    const file = fileInput.files?.[0];
                    fileInput.value = '';
                    if (!file) return;
                    imageUploadingIds.add(rec.id);
                    render();
                    events.fire('volugraph.annotation.uploadImage', { id: rec.id, file });
                });
                row.appendChild(attachBtn);
                row.appendChild(fileInput);
            };

            render();
            return { el: row, refresh: render };
        };

        const showHoverCard = (recId: string, anchorEl: HTMLElement) => {
            if (hoverCardEl && hoverCardRecId === recId) return;
            hideHoverCard();

            const rec = records.find((x) => x.id === recId);
            if (!rec) return;

            hoverCardRecId = recId;
            const card = document.createElement('div');
            card.className = isEditMode
                ? 'annotation-hover-card annotation-hover-card--edit'
                : 'annotation-hover-card annotation-hover-card--view';
            const commerceMode = isCommerceAnnotationMode();

            const num = displayIndexForId(recId);
            const badge = document.createElement('span');
            badge.className = 'annotation-hover-number';
            badge.textContent = `${num}.`;

            if (isEditMode) {
                const input = document.createElement('input');
                input.type = 'text';
                input.maxLength = 50;
                input.className = 'annotation-hover-title-input';
                input.value = rec.title || '';
                input.placeholder = commerceMode ? 'Label Name' : 'RFI title or question';

                const descInput = document.createElement('textarea');
                descInput.maxLength = 300;
                descInput.setAttribute('maxlength', '300');
                descInput.className = 'annotation-hover-desc-input';
                descInput.value = rec.text || '';
                descInput.placeholder = commerceMode
                    ? 'Product detail for shoppers (optional): dimensions, material etc.'
                    : 'Description (optional)';
                descInput.rows = 2;

                const persistDetail = (patch?: { title?: string; text?: string; status?: AnnotationPriority }) => {
                    const newTitle = patch?.title ?? input.value.trim();
                    const newText = patch?.text ?? descInput.value.slice(0, 300).trim();
                    const newStatus = patch?.status ?? rec.status;
                    if (
                        newTitle === rec.title
                        && newText === rec.text
                        && newStatus === rec.status
                    ) {
                        return;
                    }
                    rec.title = newTitle;
                    rec.text = newText;
                    rec.status = newStatus;
                    updateMarkerFlag(rec);
                    events.fire('rfi.saveDetail', {
                        id: rec.id,
                        title: newTitle,
                        text: newText,
                        imageUrl: rec.imageUrl,
                        tags: rec.tags ? [...rec.tags] : [],
                        status: newStatus
                    });
                };

                const commitFields = () => {
                    persistDetail();
                };

                card.addEventListener('focusout', (e: FocusEvent) => {
                    const next = e.relatedTarget as Node | null;
                    if (next && card.contains(next)) return;
                    commitFields();
                });

                input.addEventListener('keydown', (e: KeyboardEvent) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commitFields();
                        descInput.focus();
                    }
                });

                const applyPriority = (priority: AnnotationPriority) => {
                    persistDetail({ status: priority });
                };

                const priorityRow = document.createElement('div');
                priorityRow.className = 'annotation-hover-priority-row';
                const priorityLabel = document.createElement('span');
                priorityLabel.className = 'annotation-hover-priority-label';
                priorityLabel.textContent = getAnnotationPriorityFieldLabel(commerceMode);
                priorityRow.appendChild(priorityLabel);
                priorityRow.appendChild(
                    buildAnnotationPrioritySelect(
                        commerceMode
                            ? normalizeCommerceAnnotationHighlight(rec.status)
                            : normalizeAnnotationPriority(rec.status),
                        applyPriority,
                        commerceMode
                    )
                );

                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'annotation-hover-close';
                closeBtn.textContent = '\u00D7';
                closeBtn.title = 'Close panel';
                closeBtn.setAttribute('aria-label', 'Close annotation panel');
                closeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    commitFields();
                    setSelected(null);
                });

                const deleteBtn = document.createElement('button');
                deleteBtn.type = 'button';
                deleteBtn.className = 'annotation-hover-delete';
                deleteBtn.title = 'Delete annotation';
                deleteBtn.setAttribute('aria-label', 'Delete annotation');
                deleteBtn.innerHTML =
                    '<svg class="annotation-hover-delete-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>';
                deleteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    removeRecord(recId, false);
                });

                const actionsCol = document.createElement('div');
                actionsCol.className = 'annotation-hover-actions';
                actionsCol.appendChild(closeBtn);
                actionsCol.appendChild(deleteBtn);

                const headerRow = document.createElement('div');
                headerRow.className = 'annotation-hover-header';
                headerRow.appendChild(badge);
                headerRow.appendChild(input);

                card.appendChild(headerRow);
                card.appendChild(descInput);
                if (!commerceMode) {
                    const imageRow = buildEditImageRow(rec);
                    hoverImageRowRefresh.set(rec.id, imageRow.refresh);
                    card.appendChild(imageRow.el);
                }
                card.appendChild(priorityRow);
                card.appendChild(actionsCol);
            } else {
                const label = document.createElement('span');
                label.className = 'annotation-hover-label';
                label.textContent = rec.title || (commerceMode ? 'Product callout' : 'Request for information');

                const headerRow = document.createElement('div');
                headerRow.className = 'annotation-hover-header';
                headerRow.appendChild(badge);
                headerRow.appendChild(label);
                card.appendChild(headerRow);

                if (rec.text) {
                    const desc = document.createElement('span');
                    desc.className = 'annotation-hover-desc';
                    desc.textContent = rec.text;
                    card.appendChild(desc);
                }

                let nextRow = rec.text ? 3 : 2;
                if (!commerceMode && rec.imageUrl) {
                    const thumbWrap = document.createElement('button');
                    thumbWrap.type = 'button';
                    thumbWrap.className = 'annotation-hover-image-view';
                    const img = document.createElement('img');
                    img.src = rec.imageUrl;
                    img.alt = '';
                    thumbWrap.appendChild(img);
                    thumbWrap.addEventListener('click', (e) => {
                        e.stopPropagation();
                        openAnnotationImageLightbox(rec.imageUrl, lightboxHost());
                    });
                    thumbWrap.style.gridColumn = '2';
                    thumbWrap.style.gridRow = String(nextRow);
                    card.appendChild(thumbWrap);
                    nextRow += 1;
                }

                const priorityRow = buildAnnotationPriorityReadonly(
                    commerceMode
                        ? normalizeCommerceAnnotationHighlight(rec.status)
                        : normalizeAnnotationPriority(rec.status),
                    commerceMode
                );
                if (priorityRow) {
                    priorityRow.style.gridColumn = '2';
                    priorityRow.style.gridRow = String(nextRow);
                    card.appendChild(priorityRow);
                }
            }

            ['pointerdown', 'pointerup', 'pointermove', 'click', 'dblclick'].forEach((name) => {
                card.addEventListener(name, (e) => e.stopPropagation());
            });

            markersHost.appendChild(card);
            hoverCardEl = card;
            positionHoverCard(anchorEl);
            requestAnimationFrame(() => positionHoverCard(anchorEl));
        };

        const hideHoverCard = () => {
            if (hoverCardRecId) {
                hoverImageRowRefresh.delete(hoverCardRecId);
                imageUploadingIds.delete(hoverCardRecId);
            }
            closeAnnotationImageLightbox();
            if (hoverCardEl) {
                hoverCardEl.remove();
                hoverCardEl = null;
                hoverCardRecId = null;
            }
        };

        // ---- Marker creation ----
        const ensureMarker = (rec: RfiRecord) => {
            if (markerEls.has(rec.id)) return;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'annotation-numbered-marker';
            btn.style.pointerEvents = 'auto';
            const n = displayIndexForId(rec.id);
            const fc = statusToFlagClass(rec.status);
            btn.innerHTML = `<span class="annotation-marker-number ${fc}">${n}</span><span class="annotation-marker-pointer" aria-hidden="true"></span>`;

            btn.addEventListener('pointerdown', (e) => e.stopPropagation());
            btn.addEventListener('pointerup', (e) => e.stopPropagation());

            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (!isEditMode && selectedId === rec.id) {
                    setSelected(null);
                } else {
                    setSelected(rec.id);
                }
            });

            btn.addEventListener('dblclick', (e) => {
                e.stopPropagation();
                flyTo(rec.id);
            });

            markersHost.appendChild(btn);
            markerEls.set(rec.id, btn);
        };

        const updateMarkerPositions = () => {
            const w = scene.canvas.clientWidth;
            const h = scene.canvas.clientHeight;
            records.forEach((rec) => {
                const el = markerEls.get(rec.id);
                if (!el) return;
                const world = new Vec3(rec.position[0], rec.position[1], rec.position[2]);
                scene.camera.worldToScreen(world, screenVec);
                if (
                    screenVec.z <= 0
                    || !Number.isFinite(screenVec.x)
                    || !Number.isFinite(screenVec.y)
                ) {
                    el.classList.add('rfi-hidden');
                    return;
                }
                const sx = screenVec.x;
                const sy = screenVec.y;
                if (sx < -0.1 || sx > 1.1 || sy < -0.1 || sy > 1.1) {
                    el.classList.add('rfi-hidden');
                    return;
                }
                el.classList.remove('rfi-hidden');
                el.style.left = `${screenVec.x * w}px`;
                el.style.top = `${screenVec.y * h}px`;

                const camPos = scene.camera.mainCamera?.getPosition?.();
                if (camPos) {
                    const dist = world.distance(camPos);
                    const sr = Math.max(scene.camera.sceneRadius, 1e-4);
                    el.classList.toggle(
                        'annotation-numbered-marker--compact',
                        dist / sr > RFI_COMPACT_DISTANCE_RATIO
                    );
                } else {
                    el.classList.remove('annotation-numbered-marker--compact');
                }
            });

            if (hoverCardEl && hoverCardRecId) {
                const anchor = markerEls.get(hoverCardRecId);
                if (anchor && !anchor.classList.contains('rfi-hidden')) {
                    positionHoverCard(anchor);
                } else {
                    hideHoverCard();
                }
            }
        };

        const flyTo = (id: string) => {
            const rec = records.find((x) => x.id === id);
            if (!rec) return;
            const worldPos = new Vec3(rec.position[0], rec.position[1], rec.position[2]);
            scene.camera.setFocalPoint(worldPos);
            scene.forceRender = true;
        };

        // Legacy RFI events (panel still fires these)
        events.on('rfi.select', (id: string) => setSelected(id));
        events.on('rfi.flyTo', (id: string) => flyTo(id));
        events.on('rfi.delete', (id: string) => removeRecord(id, false));
        events.on(
            'rfi.saveDetail',
            (detail: {
                id: string;
                title: string;
                text: string;
                imageUrl: string;
                tags?: string[];
                status?: string;
                lat?: number;
                lng?: number;
                clearLatLng?: boolean;
            }) => {
            const rec = records.find((x) => x.id === detail.id);
            if (!rec) return;
            rec.title = detail.title;
            rec.text = detail.text;
            rec.imageUrl = detail.imageUrl;
            if (Array.isArray(detail.tags)) rec.tags = detail.tags;
            if ('status' in detail) {
                rec.status = resolveAnnotationStatus(detail.status);
            }
            if (detail.clearLatLng) {
                delete rec.lat;
                delete rec.lng;
            } else {
                if ('lat' in detail) {
                    if (typeof detail.lat === 'number' && Number.isFinite(detail.lat)) rec.lat = detail.lat;
                    else delete rec.lat;
                }
                if ('lng' in detail) {
                    if (typeof detail.lng === 'number' && Number.isFinite(detail.lng)) rec.lng = detail.lng;
                    else delete rec.lng;
                }
            }
            updateMarkerFlag(rec);
            const el = markerEls.get(rec.id);
            if (el) {
                el.classList.toggle('is-selected', rec.id === selectedId);
            }
            syncPanel();
            bumpAnnotationDirty();
            events.fire('volugraph.rfi', { action: 'upsert', record: { ...rec } });
        });

        // Annotation toolbar events
        events.on('annotation.flyToSelected', () => {
            if (selectedId) flyTo(selectedId);
        });

        events.on('annotation.setPriority', (priority: string) => {
            if (!selectedId) return;
            const rec = records.find((x) => x.id === selectedId);
            if (!rec) return;
            rec.status = resolveAnnotationStatus(priority);
            updateMarkerFlag(rec);
            syncPanel();
            bumpAnnotationDirty();
            events.fire('volugraph.rfi', { action: 'upsert', record: { ...rec } });
        });

        events.on('annotation.toggleListModal', () => {
            events.fire(
                'annotation.openListModal',
                records.filter((r) => r.kind !== 'coord').map((r) => ({ ...r }))
            );
        });

        events.on('annotation.selectById', (id: string) => {
            setSelected(id);
        });

        let markersGloballyVisible = true;
        const applyMarkersGlobalVisibility = () => {
            // Class toggle (not inline display) — React app CSS forces
            // `[data-volugraph-supersplat] .rfi-markers-host { display: block !important }`.
            markersHost.classList.toggle('rfi-markers-host--hidden', !markersGloballyVisible);
            if (!markersGloballyVisible) {
                hideHoverCard();
            } else {
                updateMarkerPositions();
            }
        };
        events.on('annotation.setMarkersVisible', (visible: boolean) => {
            markersGloballyVisible = visible !== false;
            applyMarkersGlobalVisibility();
        });
        events.function('annotation.markersVisible', () => markersGloballyVisible);

        events.removeFunction('annotation.hasSelection');
        events.removeFunction('annotation.getSelected');
        events.function('annotation.hasSelection', () => !!selectedId);
        events.function('annotation.getSelected', () => {
            if (!selectedId) return null;
            const rec = records.find((x) => x.id === selectedId);
            return rec ? { ...rec } : null;
        });
        events.function('annotation.getRecords', () => records.map((r) => ({ ...r })));
        events.function('rfi.canExport', () => true);

        const parseImportRecord = (raw: unknown): RfiRecord | null => {
            if (!raw || typeof raw !== 'object') return null;
            const o = raw as Record<string, unknown>;
            if (o.kind === 'coord' || o.kind === 'boundary') return null;
            const id = typeof o.id === 'string' ? o.id : genId();
            let px = 0;
            let py = 0;
            let pz = 0;
            if (Array.isArray(o.position) && o.position.length === 3) {
                px = Number(o.position[0]) || 0;
                py = Number(o.position[1]) || 0;
                pz = Number(o.position[2]) || 0;
            } else if (o.position && typeof o.position === 'object') {
                const p = o.position as Record<string, number>;
                px = Number(p.x) || 0;
                py = Number(p.y) || 0;
                pz = Number(p.z) || 0;
            }
            const cv = o.cameraView as RfiRecord['cameraView'];
            const rawTags = Array.isArray(o.tags)
                ? (o.tags as unknown[]).filter((t) => typeof t === 'string') as string[]
                : [];
            const ord = o.orderIndex;
            const latRaw = o.lat;
            const lngRaw = o.lng;
            const rec: RfiRecord = {
                id,
                kind: 'rfi',
                position: [px, py, pz],
                title: typeof o.title === 'string' ? o.title : '',
                text: typeof o.text === 'string' ? o.text : '',
                imageUrl: typeof o.imageUrl === 'string' ? o.imageUrl : '',
                imagePath: typeof o.imagePath === 'string' && o.imagePath ? o.imagePath : undefined,
                status: normalizeImportedStatus(o.status),
                tags: rawTags,
                cameraView:
                    cv && Array.isArray(cv.position) && Array.isArray(cv.target) ? cv : null,
                orderIndex: typeof ord === 'number' && Number.isFinite(ord) ? ord : undefined
            };
            if (typeof latRaw === 'number' && Number.isFinite(latRaw)) rec.lat = latRaw;
            if (typeof lngRaw === 'number' && Number.isFinite(lngRaw)) rec.lng = lngRaw;
            return rec;
        };

        const positionTupleEqual = (
            a: [number, number, number],
            b: [number, number, number]
        ) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

        const applyIncomingFields = (target: RfiRecord, incoming: RfiRecord) => {
            target.title = incoming.title;
            target.text = incoming.text;
            target.imageUrl = incoming.imageUrl;
            if (incoming.imagePath !== undefined) target.imagePath = incoming.imagePath;
            else delete target.imagePath;
            target.status = incoming.status;
            target.tags = incoming.tags;
            target.cameraView = incoming.cameraView;
            target.orderIndex = incoming.orderIndex;
            if (incoming.lat !== undefined) target.lat = incoming.lat;
            else delete target.lat;
            if (incoming.lng !== undefined) target.lng = incoming.lng;
            else delete target.lng;
        };

        events.function('rfi.import', (list: unknown) => {
            if (!Array.isArray(list)) return;
            const incoming: RfiRecord[] = [];
            for (const raw of list) {
                const rec = parseImportRecord(raw);
                if (rec) incoming.push(rec);
            }

            const prevSelected = selectedId;
            const currentRfi = records.filter((r) => r.kind !== 'coord');
            const currentIds = new Set(currentRfi.map((r) => r.id));
            const incomingIds = new Set(incoming.map((r) => r.id));
            const sameIdSet =
                currentIds.size === incomingIds.size
                && [...incomingIds].every((id) => currentIds.has(id));

            let positionsStable = sameIdSet;
            if (positionsStable) {
                for (const inc of incoming) {
                    const cur = currentRfi.find((r) => r.id === inc.id);
                    if (!cur || !positionTupleEqual(cur.position, inc.position)) {
                        positionsStable = false;
                        break;
                    }
                }
            }

            if (sameIdSet && positionsStable && currentRfi.length > 0) {
                const byId = new Map(currentRfi.map((r) => [r.id, r]));
                const merged: RfiRecord[] = [];
                for (const inc of incoming) {
                    const cur = byId.get(inc.id);
                    if (!cur) continue;
                    applyIncomingFields(cur, inc);
                    merged.push(cur);
                    updateMarkerFlag(cur);
                    const el = markerEls.get(cur.id);
                    if (el) {
                        el.classList.toggle('is-selected', cur.id === selectedId);
                    }
                    if (hoverCardRecId === cur.id) {
                        imageUploadingIds.delete(cur.id);
                        hoverImageRowRefresh.get(cur.id)?.();
                    }
                }
                records.length = 0;
                records.push(...merged);
                renumberMarkers();
                syncPanel();
                fireCountChanged();
                scene.forceRender = true;
                updateMarkerPositions();
                applyMarkersGlobalVisibility();
                lastAnnotationSaveCursor = annotationsDirtyCounter;
                events.fire('volugraph.importAnnotations', list);
                return;
            }

            records.length = 0;
            hideHoverCard();
            markerEls.forEach((el) => el.remove());
            markerEls.clear();
            selectedId = null;
            for (const rec of incoming) {
                records.push(rec);
                ensureMarker(rec);
            }
            renumberMarkers();
            syncPanel();
            events.fire('annotation.selectionChanged');
            fireCountChanged();
            scene.forceRender = true;
            updateMarkerPositions();
            if (prevSelected && records.some((r) => r.id === prevSelected)) {
                setSelected(prevSelected);
            }
            lastAnnotationSaveCursor = annotationsDirtyCounter;
            events.fire('volugraph.importAnnotations', list);
            applyMarkersGlobalVisibility();
        });

        applyMarkersGlobalVisibility();

        events.on('volugraph.annotationReorder', () => {
            bumpAnnotationDirty();
        });

        events.function('rfi.list', () =>
            records.filter((r) => r.kind !== 'coord').map((r) => ({ ...r }))
        );

        /** Full export for React-side batch flush (e.g. SAVE) — same shape as `rfi.list`. */
        events.function('rfi.export', () =>
            records.filter((r) => r.kind !== 'coord').map((r) => ({ ...r }))
        );

        const isPrimary = (e: PointerEvent) => (e.pointerType === 'mouse' ? e.button === 0 : e.isPrimary);

        const pointerdown = (e: PointerEvent) => {
            if (isPrimary(e)) clickGate = true;
        };
        const pointermove = () => {
            clickGate = false;
        };

        const pointerup = async (e: PointerEvent) => {
            if (!active || !clickGate || !isPrimary(e)) return;
            clickGate = false;
            if ((e.target as HTMLElement)?.closest?.('.rfi-panel')) return;
            if ((e.target as HTMLElement)?.closest?.('.annotation-numbered-marker')) return;
            if ((e.target as HTMLElement)?.closest?.('.annotation-hover-card')) return;
            if ((e.target as HTMLElement)?.closest?.('.annotation-modal-overlay')) return;
            if ((e.target as HTMLElement)?.closest?.('.annotation-calibration-modal')) return;
            if ((e.target as HTMLElement)?.closest?.('.coord-marker-hit')) return;
            if ((e.target as HTMLElement)?.closest?.('.coord-hover-card')) return;
            if ((e.target as HTMLElement)?.closest?.('.coord-entry-modal-overlay')) return;
            if ((e.target as HTMLElement)?.closest?.('#left-annotation-toolbar')) return;

            const ptr = pointerPositionInElement(e, scene.canvas);
            const result = await scene.camera.intersect(ptr.nx, ptr.ny);
            if (!result) return;

            const cam = cameraViewFromScene(scene);
            const id = genId();
            const world = result.position;
            let maxOrder = 0;
            for (const r of records) {
                if (typeof r.orderIndex === 'number' && r.orderIndex > maxOrder) maxOrder = r.orderIndex;
            }
            const rec: RfiRecord = {
                id,
                kind: 'rfi',
                position: [world.x, world.y, world.z],
                title: '',
                text: '',
                imageUrl: '',
                status: 'neutral',
                tags: [],
                cameraView: cam,
                orderIndex: maxOrder + 1
            };
            upsertRecord(rec, false);
            suppressCanvasDeselectClick = true;
            e.preventDefault();
            e.stopPropagation();

            events.fire('tool.deactivate');
            requestAnimationFrame(() => {
                setSelected(id);
                if (isEditMode) {
                    const inputEl = hoverCardEl?.querySelector('.annotation-hover-title-input') as HTMLInputElement | null;
                    inputEl?.focus({ preventScroll: true });
                }
            });
        };

        events.on('postrender', updateMarkerPositions);

        /** Host does not capture pointer events (full-bleed overlay would block sphere/box select UI). Pins keep pointer-events: auto. */
        const setMarkersInteractive = (hostCapturesPointer: boolean) => {
            markersHost.style.pointerEvents = hostCapturesPointer ? 'auto' : 'none';
            markerEls.forEach((el) => {
                el.style.pointerEvents = 'auto';
            });
        };

        events.on('volugraph.editModeChange', (editMode: boolean) => {
            isEditMode = editMode;
            setMarkersInteractive(false);
            hideHoverCard();
            if (editMode && selectedId) {
                setSelected(null);
            } else if (selectedId) {
                const el = markerEls.get(selectedId);
                if (el) {
                    showHoverCard(selectedId, el);
                }
            }
        });

        const volugraphRoot = (canvasContainer.dom.closest?.('[data-volugraph-embed]') ?? document.body);
        const syncEditModeFromAttr = () => {
            isEditMode = volugraphRoot.getAttribute?.('data-edit-mode') === 'true';
        };
        syncEditModeFromAttr();

        const onCanvasClickDeselect = (e: MouseEvent) => {
            if (isEditMode) return;
            if (suppressCanvasDeselectClick) {
                suppressCanvasDeselectClick = false;
                return;
            }
            if (!selectedId) return;
            const t = e.target as HTMLElement;
            if (t.closest?.('.annotation-numbered-marker')) return;
            if (t.closest?.('.annotation-hover-card')) return;
            if (t.closest?.('.rfi-panel')) return;
            if (t.closest?.('#left-annotation-toolbar')) return;
            if (t.closest?.('.annotation-modal-overlay')) return;
            if (t.closest?.('.annotation-calibration-modal')) return;
            if (t.closest?.('.coord-marker-hit')) return;
            if (t.closest?.('.coord-hover-card')) return;
            if (t.closest?.('.coord-entry-modal-overlay')) return;
            if (t.closest?.('#bottom-toolbar')) return;
            if (t.closest?.('.select-toolbar')) return;
            setSelected(null);
        };
        canvasContainer.dom.addEventListener('click', onCanvasClickDeselect);

        setMarkersInteractive(false);

        this.activate = () => {
            active = true;
            panel.setVisible(false);
            setMarkersInteractive(false);
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            syncPanel();
        };

        this.deactivate = () => {
            active = false;
            panel.setVisible(false);
            setMarkersInteractive(false);
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
        };
    }
}

export { RfiTool };
